import type { ReactNode} from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import type { ActorRefFrom} from 'xstate';
import { createActor } from 'xstate';
import type { Area} from '../../common/types/area';
import { TgArea } from '../../common/types/area';
import type { Language } from '~/common/types/language';
import { setApiInstance } from '~/features/cart/state-machine/backend-access/api';
import { cartMachine } from '~/features/cart/state-machine/cartMachine';
import { loginCart, logoutCart } from './actions/cartActions';
import { createApi } from './cartApi.client';
import { CART_MACHINE_LOGGER } from '~/features/cart/cartMachineLogger';
import type { IWebshopLoginInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { useListenToShoppingCartBroadcast } from '~/features/cart/cartBroadcast';
import {
  useSendAddCartPositionToGoogleAnalytics,
  useSendPurchaseToGoogleAnalytics,
  useSendRemoveCartPositionsToGoogleAnalytics
} from '~/tracking/googleAnalytics';
import { fallbackLng } from '~/i18nConfig';
// import { createBrowserInspector } from '@statelyai/inspect';
// import { createApiMock } from './cartApi.client.mock';

const CART_API_BASE_URL = '/resources/cart';

// Der Browser Inspector kann nur im Browser erstellt werden, auf dem Server würde ein Error-Log erfolgen ...
// let xStateInspector: ReturnType<typeof createBrowserInspector> | undefined = undefined;

// Die Statemachine soll nur im Client instanziert werden
// Jonas, 2024-12-04: Aktueller Erkenntnisstand: Die Visualisierung mit `@statelyai/inspect` Browserinspector funktioniert nur,
// wenn die state machine hier auf top-level scope instanziert wird ...
// Der Ansatz, die Statemachine in einem effect einer React Komponente zu instanzieren hat zwar technisch funktioniert,
// aber die Visualisierung hat den State nicht "live angezeigt" ... 🤷‍♂️
// export let cartActor: ReturnType<typeof createActor<typeof cartMachine>> | undefined;
export const cartActorRef: ActorRefFrom<typeof cartMachine> | undefined = createCartActorRef();

function createCartActorRef() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  // const xStateInspector = createBrowserInspector();
  const actorRef = createActor(
    cartMachine
    // { inspect: xStateInspector.inspect }
  );
  actorRef.subscribe(CART_MACHINE_LOGGER);
  actorRef.start();
  return actorRef;
}

const CartContext = createContext({ cartActorRef, cartFailure: false });

export function useCartActor() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within a CartProvider');
  }
  const { cartActorRef } = context;
  return cartActorRef;
}

export function useShowCartFailure() {
  const context = useContext(CartContext);
  const { cartFailure } = context;
  return cartFailure;
}

export function CartProvider({
  loginInfo,
  area,
  children
}: Readonly<{
  loginInfo: IWebshopLoginInfoDtoData | undefined;
  area: Area;
  children: ReactNode;
}>) {
  const { lang: langParam } = useParams() as { lang?: Language };
  const lang = langParam ?? fallbackLng;
  const [cartFailure, setCartFailure] = useState(false);
  const sendAddCartPositionToGoogleAnalytics = useSendAddCartPositionToGoogleAnalytics();
  const sendRemoveCartPositionsToGoogleAnalytics = useSendRemoveCartPositionsToGoogleAnalytics();
  const sendPurchaseToGoogleAnalytics = useSendPurchaseToGoogleAnalytics();

  useEffect(() => {
    if (area === TgArea) {
      const apiBaseUrl = `/${lang}${CART_API_BASE_URL}`;
      setApiInstance(createApi(apiBaseUrl));
      cartActorRef?.subscribe({
        error() {
          setCartFailure(true);
        }
      });
    } else {
      setApiInstance(undefined);
    }
  }, [area, lang]);

  useEffect(() => {
    if (loginInfo && area === TgArea) {
      loginCart();
    } else {
      logoutCart();
    }
  }, [loginInfo, area]);

  useListenToShoppingCartBroadcast('CART_SUCCESSFULLY_SENT', context => {
    sendPurchaseToGoogleAnalytics(context.order, loginInfo);
  });
  useListenToShoppingCartBroadcast('ADD_INTENT_PROCESSED', context => {
    if (!context.isExternalArticle && (context.successfullyAdded || context.addedWithAvailabilityOptions)) {
      sendAddCartPositionToGoogleAnalytics(
        context.articleNumber,
        context.quantity,
        context.articleDescription,
        loginInfo
      );
    }
  });
  useListenToShoppingCartBroadcast('POSITIONS_REMOVED', context => {
    sendRemoveCartPositionsToGoogleAnalytics(context.positions, loginInfo);
  });

  const contextValue = useMemo(() => ({ cartActorRef, cartFailure }), [cartFailure]);

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
}
